import type { LineItemTargets } from '../../../apis/graphql/types';
import type { TargetOption } from '../../../models/Targeting';
import type { LineItemForm } from '../../../pages/Campaigns/Form/types';
import { buildCampaignBuilderContextProps } from '../builders/buildCampaignBuilderContextProps';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_PROPERTY_KEYS,
  CAMPAIGN_BUILDER_STEP_NAMES,
  COMPONENT_OUTCOMES,
  DCM_PAGES,
} from '../constants';
import { trackAnalyticsEvent } from '../core/analyticsClient';
import type { AnalyticsEventProperties } from '../types';
import type { DcmPage } from '../types/eventMap';
import type { TargetOptionInclusionAnalytics } from '../utils/buildTargetOptionInclusionAnalytics';
import { getAppliedTargetingCategories, hasSpecificTargetingApplied } from '../utils/getAppliedTargetingCategories';
import { isBaseTargetingOnly } from '../utils/isBaseTargetingOnly';

type TargetingEventContext = {
  campaignId?: string;
  lineItemId?: string;
  adAccountId?: string;
  revisionNumber?: string;
  isValidatedEdit?: boolean;
  targetValues?: LineItemTargets | null;
  contentTargetingCatalog?: TargetOption[];
  politicalClassification?: string;
  industryVertical?: string;
  userRole?: string;
  page?: DcmPage;
};

const buildTargetingEventProps = (
  context: TargetingEventContext,
  extra?: AnalyticsEventProperties
): AnalyticsEventProperties => ({
  [ANALYTICS_PROPERTY_KEYS.PAGE]: context.page ?? DCM_PAGES.CAMPAIGN_BUILDER,
  [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
  ...buildCampaignBuilderContextProps({
    politicalClassification: context.politicalClassification,
    industryVertical: context.industryVertical,
    userRole: context.userRole,
  }),
  ...extra,
});

const buildTargetingSaveEventProps = (
  context: TargetingEventContext,
  extra?: AnalyticsEventProperties
): AnalyticsEventProperties =>
  buildTargetingEventProps(context, {
    [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: context.campaignId,
    [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: context.lineItemId,
    [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: context.adAccountId,
    [ANALYTICS_PROPERTY_KEYS.REVISION_NUMBER]: context.revisionNumber,
    [ANALYTICS_PROPERTY_KEYS.IS_VALIDATED_EDIT]: context.isValidatedEdit,
    ...extra,
  });

export type TrackBaseTargetingAppliedParams = TargetingEventContext & {
  formValues: LineItemForm;
};

export const trackBaseTargetingApplied = ({ formValues, ...context }: TrackBaseTargetingAppliedParams): void => {
  if (!isBaseTargetingOnly(formValues)) {
    return;
  }

  trackAnalyticsEvent(ANALYTICS_EVENTS.BASE_TARGETING_APPLIED, {
    ...buildTargetingSaveEventProps(context, {
      [ANALYTICS_PROPERTY_KEYS.IS_BASE_TARGETING]: true,
    }),
  });
};

export type TrackAudiencesSelectionUpdatedParams = TargetingEventContext & {
  audiencesAddedCount: number;
  audiencesRemovedCount: number;
  audienceCount: number;
  audienceIds: string[];
  audienceNames: string[];
  audienceIdsAdded: string[];
  audienceIdsRemoved: string[];
  audienceNamesAdded: string[];
  audienceNamesRemoved: string[];
};

export const trackAudiencesSelectionUpdated = ({
  audiencesAddedCount,
  audiencesRemovedCount,
  audienceCount,
  audienceIds,
  audienceNames,
  audienceIdsAdded,
  audienceIdsRemoved,
  audienceNamesAdded,
  audienceNamesRemoved,
  ...context
}: TrackAudiencesSelectionUpdatedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.AUDIENCE_SELECTED, {
    ...buildTargetingEventProps(context, {
      [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: context.campaignId,
      [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: context.lineItemId,
      [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: context.adAccountId,
      [ANALYTICS_PROPERTY_KEYS.STEP_NAME]: CAMPAIGN_BUILDER_STEP_NAMES.TARGETING,
      [ANALYTICS_PROPERTY_KEYS.AUDIENCES_ADDED_COUNT]: audiencesAddedCount,
      [ANALYTICS_PROPERTY_KEYS.AUDIENCES_REMOVED_COUNT]: audiencesRemovedCount,
      [ANALYTICS_PROPERTY_KEYS.AUDIENCE_COUNT]: audienceCount,
      [ANALYTICS_PROPERTY_KEYS.AUDIENCE_IDS]: audienceIds,
      [ANALYTICS_PROPERTY_KEYS.AUDIENCE_NAMES]: audienceNames,
      [ANALYTICS_PROPERTY_KEYS.AUDIENCE_IDS_ADDED]: audienceIdsAdded,
      [ANALYTICS_PROPERTY_KEYS.AUDIENCE_IDS_REMOVED]: audienceIdsRemoved,
      [ANALYTICS_PROPERTY_KEYS.AUDIENCE_NAMES_ADDED]: audienceNamesAdded,
      [ANALYTICS_PROPERTY_KEYS.AUDIENCE_NAMES_REMOVED]: audienceNamesRemoved,
    }),
  });
};

export type TrackGenreSelectionUpdatedParams = TargetingEventContext & {
  genresAddedCount: number;
  genresRemovedCount: number;
  genreCount: number;
  genreIds: string[];
  genreNames: string[];
  genreIdsAdded: string[];
  genreIdsRemoved: string[];
  genreNamesAdded: string[];
  genreNamesRemoved: string[];
} & TargetOptionInclusionAnalytics;

export const trackGenreSelectionUpdated = ({
  genresAddedCount,
  genresRemovedCount,
  genreCount,
  genreIds,
  genreNames,
  genreIdsAdded,
  genreIdsRemoved,
  genreNamesAdded,
  genreNamesRemoved,
  includedNames,
  excludedNames,
  ...context
}: TrackGenreSelectionUpdatedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.GENRE_SELECTED, {
    ...buildTargetingEventProps(context, {
      [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: context.campaignId,
      [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: context.lineItemId,
      [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: context.adAccountId,
      [ANALYTICS_PROPERTY_KEYS.STEP_NAME]: CAMPAIGN_BUILDER_STEP_NAMES.TARGETING,
      [ANALYTICS_PROPERTY_KEYS.GENRE_COUNT]: genreCount,
      [ANALYTICS_PROPERTY_KEYS.GENRE_IDS]: genreIds,
      [ANALYTICS_PROPERTY_KEYS.GENRE_NAMES]: genreNames,
      [ANALYTICS_PROPERTY_KEYS.GENRE_IDS_ADDED]: genreIdsAdded,
      [ANALYTICS_PROPERTY_KEYS.GENRE_IDS_REMOVED]: genreIdsRemoved,
      [ANALYTICS_PROPERTY_KEYS.GENRE_ADDED_NAME]: genreNamesAdded,
      [ANALYTICS_PROPERTY_KEYS.GENRE_REMOVED_NAME]: genreNamesRemoved,
      [ANALYTICS_PROPERTY_KEYS.GENRE_ADDED_COUNT]: genresAddedCount,
      [ANALYTICS_PROPERTY_KEYS.GENRE_REMOVED_COUNT]: genresRemovedCount,
      [ANALYTICS_PROPERTY_KEYS.INCLUDED_GENRE_NAMES]: includedNames,
      [ANALYTICS_PROPERTY_KEYS.EXCLUDED_GENRE_NAMES]: excludedNames,
    }),
  });
};

export type TrackLocationSelectionUpdatedParams = TargetingEventContext & {
  locationsAddedCount: number;
  locationsRemovedCount: number;
  locationCount: number;
  locationIds: string[];
  locationNames: string[];
  locationIdsAdded: string[];
  locationIdsRemoved: string[];
  locationNamesAdded: string[];
  locationNamesRemoved: string[];
} & TargetOptionInclusionAnalytics;

export const trackLocationSelectionUpdated = ({
  locationsAddedCount,
  locationsRemovedCount,
  locationCount,
  locationIds,
  locationNames,
  locationIdsAdded,
  locationIdsRemoved,
  locationNamesAdded,
  locationNamesRemoved,
  includedNames,
  excludedNames,
  ...context
}: TrackLocationSelectionUpdatedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.LOCATION_SELECTED, {
    ...buildTargetingEventProps(context, {
      [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: context.campaignId,
      [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: context.lineItemId,
      [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: context.adAccountId,
      [ANALYTICS_PROPERTY_KEYS.STEP_NAME]: CAMPAIGN_BUILDER_STEP_NAMES.TARGETING,
      [ANALYTICS_PROPERTY_KEYS.LOCATION_SELECTION_COUNT]: locationCount,
      [ANALYTICS_PROPERTY_KEYS.LOCATION_IDS]: locationIds,
      [ANALYTICS_PROPERTY_KEYS.LOCATION_NAMES]: locationNames,
      [ANALYTICS_PROPERTY_KEYS.LOCATION_IDS_ADDED]: locationIdsAdded,
      [ANALYTICS_PROPERTY_KEYS.LOCATION_IDS_REMOVED]: locationIdsRemoved,
      [ANALYTICS_PROPERTY_KEYS.LOCATION_ADDED_NAME]: locationNamesAdded,
      [ANALYTICS_PROPERTY_KEYS.LOCATION_REMOVED_NAME]: locationNamesRemoved,
      [ANALYTICS_PROPERTY_KEYS.LOCATION_ADDED_COUNT]: locationsAddedCount,
      [ANALYTICS_PROPERTY_KEYS.LOCATION_REMOVED_COUNT]: locationsRemovedCount,
      [ANALYTICS_PROPERTY_KEYS.INCLUDED_LOCATION_NAMES]: includedNames,
      [ANALYTICS_PROPERTY_KEYS.EXCLUDED_LOCATION_NAMES]: excludedNames,
    }),
  });
};

export type TrackPremiumContentTargetingParams = TargetingEventContext & {
  formValues: LineItemForm;
};

export const trackPremiumContentTargeting = ({ formValues, ...context }: TrackPremiumContentTargetingParams): void => {
  if (!hasSpecificTargetingApplied(formValues)) {
    return;
  }

  const targetingTypes = getAppliedTargetingCategories(formValues);

  trackAnalyticsEvent(ANALYTICS_EVENTS.PREMIUM_CONTENT_TARGETING, {
    ...buildTargetingSaveEventProps(context, {
      [ANALYTICS_PROPERTY_KEYS.HAS_PREMIUM_CONTENT_TARGETING]: true,
      [ANALYTICS_PROPERTY_KEYS.TARGETING_TYPES]: targetingTypes,
    }),
  });
};

export type TrackAdFormatSelectedParams = TargetingEventContext & {
  adFormat: string;
  adDuration?: string;
};

export const trackAdFormatSelected = ({ adFormat, adDuration, ...context }: TrackAdFormatSelectedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.AD_FORMAT_SELECTED, {
    ...buildTargetingEventProps(context, {
      [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: context.campaignId,
      [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: context.lineItemId,
      [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: context.adAccountId,
      [ANALYTICS_PROPERTY_KEYS.STEP_NAME]: CAMPAIGN_BUILDER_STEP_NAMES.ADS,
      [ANALYTICS_PROPERTY_KEYS.AD_FORMAT]: adFormat,
      [ANALYTICS_PROPERTY_KEYS.AD_DURATION]: adDuration,
      [ANALYTICS_PROPERTY_KEYS.REVISION_NUMBER]: context.revisionNumber,
    }),
  });
};
